/**
 * Correlation identifiers for the `pwa_logs` telemetry spine.
 *
 * Four identifiers, because each answers a different question and collapsing
 * any two loses information (PLAN.md §3.1):
 *
 *   journeyId       localStorage    one scan attempt, across reloads, browser
 *                                   close, AND uid changes
 *   tabId           sessionStorage  one browser tab, across reloads
 *   pageLoadId      module const    one JS context
 *   journeyPageLoad localStorage    0,1,2… page loads within this journey
 *
 * `journeyId` — not `userId` — is the session key. Under the iOS re-auth
 * failure mode (Mode G, see HERE_BE_DRAGONS.md), `signInAnonymously` can mint a
 * DIFFERENT anon uid mid-session, so one user journey can split across two
 * uids; and when auth fails outright every broken session shares the literal
 * uid `"anonymous"`. Neither can key a session.
 *
 * `journeyPageLoad` is the clock-free ordering key. Do not order a journey by
 * `timestamp` (device clock, can be wrong or NTP-corrected mid-session) or by
 * `serverTs` (resolves at commit, and commits reorder). See PLAN.md §4.2. It is
 * also directly diagnostic: `max(journeyPageLoad) >= 3` means the journey
 * reloaded three times, which is the tab-eviction signature CHANGELOG [2.4.16]
 * had to detect by eye.
 *
 * EVERY storage access here is wrapped. Safari private mode, ITP eviction, and
 * quota-exceeded all throw on plain `localStorage.setItem`, and telemetry must
 * never be able to break the app. On total storage failure we degrade to
 * in-memory ids: the page load is still internally consistent, it just cannot
 * be stitched to its siblings.
 */

const JOURNEY_ID_KEY = "ttf.telemetry.journeyId";
const JOURNEY_TOUCHED_AT_KEY = "ttf.telemetry.journeyTouchedAt";
const JOURNEY_PAGE_LOAD_KEY = "ttf.telemetry.journeyPageLoad";
const TAB_ID_KEY = "ttf.telemetry.tabId";

/**
 * A journey is considered stale — and a fresh one minted — when the previous
 * page load in it ended more than this long ago. Measured from `touchedAt`
 * (refreshed once per page load, not per log row: refreshing per row would be a
 * localStorage write on every `logInfo`).
 *
 * Two hours is long enough to survive a user putting the phone down mid-scan
 * and coming back, short enough that tomorrow's scan is a new journey. Stage 2
 * additionally calls `startNewJourney()` at explicit lifecycle boundaries
 * (submit / discard), which is the primary mechanism; this TTL is the net for
 * journeys that never reach one.
 */
const JOURNEY_IDLE_MS = 2 * 60 * 60 * 1000;

/* ------------------------------------------------------------------ */
/* Storage helpers — never throw                                       */
/* ------------------------------------------------------------------ */

function readStore(store: "local" | "session", key: string): string | null {
  try {
    const s = store === "local" ? window.localStorage : window.sessionStorage;
    return s.getItem(key);
  } catch {
    return null;
  }
}

function writeStore(store: "local" | "session", key: string, value: string): void {
  try {
    const s = store === "local" ? window.localStorage : window.sessionStorage;
    s.setItem(key, value);
  } catch {
    /* private mode / quota — degrade to in-memory, never throw */
  }
}

function removeStore(store: "local" | "session", key: string): void {
  try {
    const s = store === "local" ? window.localStorage : window.sessionStorage;
    s.removeItem(key);
  } catch {
    /* ignore */
  }
}

/* ------------------------------------------------------------------ */
/* Id generation                                                       */
/* ------------------------------------------------------------------ */

/**
 * `crypto.randomUUID()` is unavailable on iOS Safari < 15.4 and, critically,
 * on ANY non-secure context — which includes plain-HTTP local dev
 * (`npm start`). `crypto.getRandomValues` has much wider support but is also
 * secure-context-gated in some engines, so there is a final `Math.random`
 * fallback. Ids only need to be collision-free within a user's own storage,
 * not globally unique, so a weak source is acceptable at the tail.
 */
function newId(): string {
  try {
    const c: Crypto | undefined =
      typeof crypto !== "undefined" ? crypto : undefined;
    if (c && typeof (c as any).randomUUID === "function") {
      return (c as any).randomUUID();
    }
    if (c && typeof c.getRandomValues === "function") {
      const bytes = new Uint8Array(16);
      c.getRandomValues(bytes);
      let out = "";
      for (let i = 0; i < bytes.length; i++) {
        out += (bytes[i] + 0x100).toString(16).slice(1);
      }
      return out;
    }
  } catch {
    /* fall through */
  }
  return (
    Date.now().toString(36) +
    Math.random().toString(36).slice(2, 10) +
    Math.random().toString(36).slice(2, 10)
  );
}

/* ------------------------------------------------------------------ */
/* Resolution — runs ONCE at module init                               */
/* ------------------------------------------------------------------ */

/** New on every page load. Never persisted. */
const pageLoadId = newId();

function resolveTabId(): string {
  const existing = readStore("session", TAB_ID_KEY);
  if (existing) return existing;
  const fresh = newId();
  writeStore("session", TAB_ID_KEY, fresh);
  return fresh;
}

function resolveJourney(): { journeyId: string; journeyPageLoad: number } {
  const now = Date.now();
  const existingId = readStore("local", JOURNEY_ID_KEY);
  const touchedRaw = readStore("local", JOURNEY_TOUCHED_AT_KEY);
  const touchedAt = touchedRaw ? parseInt(touchedRaw, 10) : NaN;

  // A journey whose last page load was long ago is stale. `isNaN` also covers a
  // partially-written pair (id present, timestamp missing or corrupt), which is
  // treated as stale rather than trusted — a wrong stitch is worse than a split.
  const stale =
    !existingId || isNaN(touchedAt) || now - touchedAt > JOURNEY_IDLE_MS;

  if (stale) {
    const fresh = newId();
    writeStore("local", JOURNEY_ID_KEY, fresh);
    writeStore("local", JOURNEY_TOUCHED_AT_KEY, String(now));
    writeStore("local", JOURNEY_PAGE_LOAD_KEY, "0");
    return { journeyId: fresh, journeyPageLoad: 0 };
  }

  const prevRaw = readStore("local", JOURNEY_PAGE_LOAD_KEY);
  const prev = prevRaw ? parseInt(prevRaw, 10) : NaN;
  const next = isNaN(prev) || prev < 0 ? 0 : prev + 1;

  writeStore("local", JOURNEY_TOUCHED_AT_KEY, String(now));
  writeStore("local", JOURNEY_PAGE_LOAD_KEY, String(next));
  return { journeyId: existingId, journeyPageLoad: next };
}

const tabId = resolveTabId();
let { journeyId, journeyPageLoad } = resolveJourney();

/* ------------------------------------------------------------------ */
/* Sequence                                                            */
/* ------------------------------------------------------------------ */

let seqCounter = 0;

/**
 * Monotonic within this page load, assigned at EMIT time.
 *
 * Assigning at emit rather than at commit is the whole point: Firestore writes
 * are async (one `setDoc` per row today — batching was planned and not built),
 * so commit order does not match emit order. `(journeyPageLoad, seq)` is the
 * only total order that survives that.
 */
export function nextSeq(): number {
  return seqCounter++;
}

/* ------------------------------------------------------------------ */
/* Public accessors                                                    */
/* ------------------------------------------------------------------ */

/**
 * A fresh Firestore document id for one `pwa_logs` row.
 *
 * Generated on the client so an outbox re-send can `setDoc` the SAME id and
 * overwrite rather than duplicate. Random (never sequential), so it keeps the
 * write distribution that `addDoc`'s auto-ids exist to provide.
 */
export function newRowId(): string {
  return newId();
}

export function getPageLoadId(): string {
  return pageLoadId;
}

export function getTabId(): string {
  return tabId;
}

export function getJourneyId(): string {
  return journeyId;
}

export function getJourneyPageLoad(): number {
  return journeyPageLoad;
}

/**
 * Force a new journey. Call at explicit lifecycle boundaries — a scan is
 * submitted, discarded, or a brand-new attempt starts — so two consecutive
 * scans by the same person are two journeys rather than one.
 *
 * Resets `journeyPageLoad` to 0 because the counter is scoped to the journey.
 * Does NOT reset `seq`: `seq` is scoped to the page load, and a page load can
 * span two journeys.
 */
type JourneyChangeListener = () => void;
const journeyChangeListeners: JourneyChangeListener[] = [];

/**
 * Run `cb` immediately BEFORE the journey id rotates. The phase model uses this
 * to close whatever phase is open inside the journey that opened it — a
 * `phase.exit` emitted after the rotation would land in the new journey and
 * orphan its `phase.enter`.
 */
export function onJourneyChange(cb: JourneyChangeListener): () => void {
  journeyChangeListeners.push(cb);
  return () => {
    const i = journeyChangeListeners.indexOf(cb);
    if (i !== -1) journeyChangeListeners.splice(i, 1);
  };
}

export function startNewJourney(): string {
  for (let i = 0; i < journeyChangeListeners.length; i++) {
    try {
      journeyChangeListeners[i]();
    } catch {
      /* a listener must never block the boundary */
    }
  }
  const fresh = newId();
  journeyId = fresh;
  journeyPageLoad = 0;
  writeStore("local", JOURNEY_ID_KEY, fresh);
  writeStore("local", JOURNEY_TOUCHED_AT_KEY, String(Date.now()));
  writeStore("local", JOURNEY_PAGE_LOAD_KEY, "0");
  return fresh;
}

/** Test-only. Clears persisted journey/tab state. */
export function __resetTelemetryIdsForTest(): void {
  removeStore("local", JOURNEY_ID_KEY);
  removeStore("local", JOURNEY_TOUCHED_AT_KEY);
  removeStore("local", JOURNEY_PAGE_LOAD_KEY);
  removeStore("session", TAB_ID_KEY);
  seqCounter = 0;
  journeyChangeListeners.length = 0;
}
